import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useAnnouncements,
  useUnreadAnnouncementCount,
  useMarkAnnouncementAsRead,
} from '@sphere/communication/data-access'
import { AnnouncementPriority } from '@sphere/communication/domain'
import type { Notification } from '@sphere/shared/ui/design-system'

/**
 * Mappe les annonces (communication-service) sur la forme attendue par le TopBar.
 * Le filtrage par classe/rôle/établissement est déjà fait côté serveur
 * (voir announcement-query.builder.ts) — on affiche simplement ce que l'API renvoie.
 */
function toPriorityType(priority: AnnouncementPriority): Notification['type'] {
  switch (priority) {
    case AnnouncementPriority.URGENT:
      return 'error'
    case AnnouncementPriority.HIGH:
      return 'warning'
    default:
      return 'info'
  }
}

export function useTopBarNotifications() {
  const navigate = useNavigate()

  const { data } = useAnnouncements({ limit: 5 })
  const { data: unreadCount } = useUnreadAnnouncementCount()
  const markAsRead = useMarkAnnouncementAsRead()

  const notifications = useMemo<Notification[]>(
    () =>
      (data?.data ?? []).map((announcement) => ({
        id: announcement.id,
        title: announcement.title,
        description: announcement.content,
        timestamp: new Date(announcement.publishedAt ?? announcement.createdAt),
        read: announcement.isRead,
        type: toPriorityType(announcement.priority),
      })),
    [data]
  )

  const onNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead.mutate(notification.id)
    }
    navigate('/notifications')
  }

  return {
    notifications,
    unreadCount: unreadCount ?? 0,
    onNotificationClick,
  }
}
